import { useState, useCallback } from 'react';
import { X, Minimize2, ChevronRight, File, Folder } from 'lucide-react';
import clsx from 'clsx';

export interface FileTab {
  id: string;
  filePath: string;
  fileName: string;
  isActive: boolean;
  isModified?: boolean;
  language?: string;
}

export interface FullScreenArtifactsProps {
  isFullScreen: boolean;
  onToggleFullScreen: () => void;
  onClose: () => void;
  tabs: FileTab[];
  activeTabId?: string;
  onTabClick: (tabId: string) => void;
  onTabClose: (tabId: string) => void;
  children: React.ReactNode;
  className?: string;
}

export function FullScreenArtifacts({
  isFullScreen,
  onToggleFullScreen,
  onClose,
  tabs,
  activeTabId,
  onTabClick,
  onTabClose,
  children,
  className
}: FullScreenArtifactsProps) {
  if (!isFullScreen) {
    return <>{children}</>;
  }

  const activeTab = tabs.find(tab => tab.id === activeTabId);

  return (
    <div className={clsx(
      'fixed inset-0 z-50 bg-white dark:bg-gray-900 flex flex-col',
      className
    )}>
      {/* Header Bar */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Artifacts View
          </h2>
          
          {/* Breadcrumb Navigation */}
          {activeTab && (
            <BreadcrumbNavigation filePath={activeTab.filePath} />
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onToggleFullScreen}
            className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md transition-colors"
            title="Minimize"
          >
            <Minimize2 className="size-4" />
          </button>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md transition-colors"
            title="Close"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>

      {/* File Tabs */}
      {tabs.length > 0 && (
        <div className="flex items-center border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="flex-1 flex overflow-x-auto">
            {tabs.map(tab => (
              <FileTabButton
                key={tab.id}
                tab={tab}
                isActive={tab.id === activeTabId}
                onClick={() => onTabClick(tab.id)}
                onClose={() => onTabClose(tab.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Content Area */}
      <div className="flex-1 overflow-hidden">
        {children}
      </div>
    </div>
  );
}

/**
 * Breadcrumb Navigation Component
 */
interface BreadcrumbNavigationProps {
  filePath: string;
}

function BreadcrumbNavigation({ filePath }: BreadcrumbNavigationProps) {
  const pathParts = filePath.split('/').filter(Boolean);
  
  return (
    <nav className="flex items-center text-sm text-gray-600 dark:text-gray-400">
      <Folder className="size-4 mr-1" />
      {pathParts.map((part, index) => {
        const isLast = index === pathParts.length - 1;
        const isFile = isLast && part.includes('.');
        
        return (
          <div key={index} className="flex items-center">
            {index > 0 && <ChevronRight className="size-3 mx-1" />}
            <span className={clsx(
              'flex items-center gap-1',
              isLast ? 'text-gray-900 dark:text-gray-100 font-medium' : 'hover:text-gray-800 dark:hover:text-gray-200'
            )}>
              {isFile && <File className="size-3" />}
              {!isFile && index < pathParts.length - 1 && <Folder className="size-3" />}
              {part}
            </span>
          </div>
        );
      })}
    </nav>
  );
}

/**
 * File Tab Button Component
 */
interface FileTabButtonProps {
  tab: FileTab;
  isActive: boolean;
  onClick: () => void;
  onClose: () => void;
}

function FileTabButton({ tab, isActive, onClick, onClose }: FileTabButtonProps) {
  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'js':
      case 'jsx':
      case 'ts':
      case 'tsx':
        return '📄';
      case 'css':
      case 'scss':
      case 'sass':
        return '🎨';
      case 'html':
      case 'htm':
        return '🌐';
      case 'json':
        return '📋';
      case 'md':
      case 'markdown':
        return '📝';
      case 'py':
        return '🐍';
      case 'java':
        return '☕';
      case 'cpp':
      case 'c':
        return '⚙️';
      default:
        return '📄';
    }
  };

  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex items-center gap-2 px-4 py-2 border-r border-gray-200 dark:border-gray-700 transition-colors min-w-0',
        {
          'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-b-2 border-blue-500': isActive,
          'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700': !isActive
        }
      )}
    >
      <span className="text-sm">{getFileIcon(tab.fileName)}</span>
      <span className="text-sm font-medium truncate max-w-32">
        {tab.fileName}
      </span>
      {tab.isModified && (
        <div className="size-2 bg-orange-500 rounded-full flex-shrink-0" />
      )}
      <button
        onClick={handleClose}
        className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors flex-shrink-0"
        title="Close tab"
      >
        <X className="size-3" />
      </button>
    </button>
  );
}

/**
 * Hook for managing full-screen artifacts state
 */
export function useFullScreenArtifacts() {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [tabs, setTabs] = useState<FileTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | undefined>();

  const toggleFullScreen = useCallback(() => {
    setIsFullScreen(prev => !prev);
  }, []);

  const enterFullScreen = useCallback(() => {
    setIsFullScreen(true);
  }, []);

  const exitFullScreen = useCallback(() => {
    setIsFullScreen(false);
  }, []);

  const addTab = useCallback((filePath: string, fileName?: string, language?: string) => {
    const tabId = `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newTab: FileTab = {
      id: tabId,
      filePath,
      fileName: fileName || filePath.split('/').pop() || 'Untitled',
      isActive: false,
      language
    };

    setTabs(prev => {
      // Check if tab already exists
      const existingTab = prev.find(tab => tab.filePath === filePath);
      if (existingTab) {
        setActiveTabId(existingTab.id);
        return prev;
      }

      // Add new tab
      const newTabs = [...prev, newTab];
      setActiveTabId(tabId);
      return newTabs;
    });

    return tabId;
  }, []);

  const removeTab = useCallback((tabId: string) => {
    setTabs(prev => {
      const newTabs = prev.filter(tab => tab.id !== tabId);
      
      // If removing active tab, switch to next available tab
      if (activeTabId === tabId && newTabs.length > 0) {
        setActiveTabId(newTabs[0].id);
      } else if (newTabs.length === 0) {
        setActiveTabId(undefined);
      }
      
      return newTabs;
    });
  }, [activeTabId]);

  const switchToTab = useCallback((tabId: string) => {
    setActiveTabId(tabId);
  }, []);

  const markTabModified = useCallback((tabId: string, isModified: boolean) => {
    setTabs(prev => prev.map(tab => 
      tab.id === tabId ? { ...tab, isModified } : tab
    ));
  }, []);

  const clearTabs = useCallback(() => {
    setTabs([]);
    setActiveTabId(undefined);
  }, []);

  const getActiveTab = useCallback(() => {
    return tabs.find(tab => tab.id === activeTabId);
  }, [tabs, activeTabId]);

  return {
    isFullScreen,
    tabs,
    activeTabId,
    toggleFullScreen,
    enterFullScreen,
    exitFullScreen,
    addTab,
    removeTab,
    switchToTab,
    markTabModified,
    clearTabs,
    getActiveTab
  };
}
